(() => {
  if (window.ResinSwatchSampler) {
    window.ResinSwatchSampler.scan();
    return;
  }

  const cache = new Map();
  const approvedColors = new Map([
    ['gray oak and white', '#dfe1e4'],
    ['walnut and black', '#393939'],
    ['walnut and taupe', '#d5c8be'],
    ['gray oak and taupe', '#a29a90']
  ]);
  const namedColors = [
    ['charcoal', '#343434'], ['black', '#171717'], ['espresso', '#3b2922'],
    ['navy', '#24344d'], ['blue', '#46779b'], ['teal', '#3f7f7c'],
    ['olive', '#777654'], ['green', '#66806a'], ['burgundy', '#713d46'],
    ['red', '#a3483f'], ['orange', '#c87845'], ['yellow', '#d8b84f'],
    ['purple', '#76617f'], ['pink', '#d5a9ab'], ['gold', '#b99a55'],
    ['silver', '#b8b9b7'], ['bronze', '#8a6947'], ['copper', '#a66a4e'],
    ['taupe', '#a79d91'], ['beige', '#c8bca8'], ['cream', '#e6ddcc'],
    ['ivory', '#ece8dc'], ['white', '#e7e7e3'], ['grey', '#8a8b89'],
    ['gray', '#8a8b89'], ['brown', '#745844'], ['walnut', '#76503b'],
    ['oak', '#b6956c'], ['natural', '#c8b28e'], ['clear', '#dce7e8']
  ];
  const semanticAliases = [
    ['night sky', '#202a3a'], ['midnight', '#1d2738'], ['smoke', '#554f4a'],
    ['rose gold', '#b98278'], ['champagne', '#c7ad7d'], ['chestnut', '#754932'],
    ['matte', '#817b74'], ['satin', '#aaa096'], ['gloss', '#d4cec5'],
    ['caribbean', '#238fac'], ['ocean', '#287f9d'], ['aqua', '#35a5ad'],
    ['turquoise', '#32a1a3'], ['jade', '#4f806b'], ['merlot', '#743e4c'],
    ['onyx', '#252525'], ['ember', '#3a302c'], ['quartz', '#b99a55'],
    ['northern lights', '#397e85'], ['marble', '#ddd9cf']
  ];
  const materialNames = new Set(['walnut', 'oak', 'natural', 'brown']);
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
  const luminance = (rgb) => (rgb[0] * 0.2126) + (rgb[1] * 0.7152) + (rgb[2] * 0.0722);

  function hexToRgb(hex) {
    const value = hex.replace('#', '');
    return [0, 2, 4].map((index) => parseInt(value.slice(index, index + 2), 16));
  }

  function semanticTarget(label) {
    const lowerLabel = label.toLowerCase();
    const alias = semanticAliases.find(([name]) => lowerLabel.includes(name));
    if (alias) return hexToRgb(alias[1]);
    const matches = namedColors.filter(([name]) => lowerLabel.includes(name));
    const finishMatch = matches.find(([name]) => !materialNames.has(name));
    return matches.length ? hexToRgb((finishMatch || matches[0])[1]) : null;
  }

  function guaranteedFallback(label) {
    const approved = approvedColors.get(label.trim().toLowerCase());
    if (approved) return approved;
    const target = semanticTarget(label);
    if (target) return `rgb(${target.join(', ')})`;

    // Unknown creative finish names still receive a restrained, deterministic
    // color while their variant photograph loads (or when no image was supplied).
    const palette = ['#746d64', '#8d8175', '#6f7775', '#727784', '#8a746d', '#68766e', '#9a8d78'];
    let hash = 0;
    for (const character of label.toLowerCase()) hash = ((hash * 31) + character.charCodeAt(0)) >>> 0;
    return palette[hash % palette.length];
  }

  function edgeColor(data, width, height) {
    const pixels = [];
    for (let x = 0; x < width; x += 2) {
      for (const y of [0, height - 1]) {
        const i = (y * width + x) * 4;
        if (data[i + 3] > 200) pixels.push([data[i], data[i + 1], data[i + 2]]);
      }
    }
    for (let y = 0; y < height; y += 2) {
      for (const x of [0, width - 1]) {
        const i = (y * width + x) * 4;
        if (data[i + 3] > 200) pixels.push([data[i], data[i + 1], data[i + 2]]);
      }
    }
    if (!pixels.length) return [255, 255, 255];
    return [0, 1, 2].map((channel) => {
      const values = pixels.map((pixel) => pixel[channel]).sort((a, b) => a - b);
      return values[Math.floor(values.length / 2)];
    });
  }

  function sampledColor(image, label) {
    const size = 48;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, size, size);
    const pixels = context.getImageData(0, 0, size, size).data;
    const background = edgeColor(pixels, size, size);
    const buckets = new Map();
    const lowerLabel = label.toLowerCase();
    const target = semanticTarget(lowerLabel);
    const wantsLight = /white|ivory|cream|natural/.test(lowerLabel);
    const wantsDark = /black|charcoal|espresso/.test(lowerLabel);

    for (let y = 3; y < size - 3; y += 1) {
      for (let x = 3; x < size - 3; x += 1) {
        const i = (y * size + x) * 4;
        if (pixels[i + 3] < 220) continue;
        const rgb = [pixels[i], pixels[i + 1], pixels[i + 2]];
        const bgDistance = distance(rgb, background);
        const light = luminance(rgb);
        if (bgDistance < 20 || (light > 247 && bgDistance < 45)) continue;

        const saturation = Math.max(...rgb) - Math.min(...rgb);
        const centerDistance = Math.hypot(x - size / 2, y - size / 2) / (size * 0.7);
        let weight = 1.75 - clamp(centerDistance, 0, 1.25);
        weight *= 1 + (saturation / 510);
        if (wantsLight) weight *= 0.55 + (light / 255);
        if (wantsDark) weight *= 1.65 - (light / 255);

        const key = `${rgb[0] >> 4}-${rgb[1] >> 4}-${rgb[2] >> 4}`;
        const bucket = buckets.get(key) || { weight: 0, red: 0, green: 0, blue: 0 };
        bucket.weight += weight;
        bucket.red += rgb[0] * weight;
        bucket.green += rgb[1] * weight;
        bucket.blue += rgb[2] * weight;
        buckets.set(key, bucket);
      }
    }

    const candidates = [...buckets.values()].map((bucket) => {
      const rgb = [bucket.red, bucket.green, bucket.blue].map((channel) => channel / bucket.weight);
      let score = Math.log1p(bucket.weight) * 16;
      if (target) score -= distance(rgb, target) * 0.9;
      return { ...bucket, rgb, score };
    });
    const winner = candidates.sort((a, b) => b.score - a.score)[0];
    if (!winner) return null;
    const rgb = winner.rgb.map(Math.round);
    return `rgb(${rgb.join(', ')})`;
  }

  function sample(swatch) {
    if (swatch.dataset.rsSwatchSampled === 'true') return;
    swatch.dataset.rsSwatchSampled = 'true';
    const source = swatch.dataset.rsSwatchSource;

    const apply = (color) => {
      if (!color) return;
      swatch.style.setProperty('--swatch-background', color, 'important');
      swatch.style.removeProperty('--swatch-background-image');
      swatch.classList.remove('with-image');
      swatch.dataset.rsSampledColor = color;
    };

    const approvedColor = approvedColors.get((swatch.title || '').trim().toLowerCase());
    if (approvedColor) {
      apply(approvedColor);
      return;
    }

    apply(guaranteedFallback(swatch.title || 'finish'));

    if (!source) {
      return;
    }

    if (cache.has(source)) {
      apply(cache.get(source));
      return;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = () => {
      try {
        const color = sampledColor(image, swatch.title || '');
        cache.set(source, color);
        apply(color);
      } catch (_) {
        swatch.dataset.rsSwatchSampled = 'false';
      }
    };
    image.onerror = () => { swatch.dataset.rsSwatchSampled = 'false'; };
    image.src = source;
  }

  function scan(root = document) {
    root.querySelectorAll('.color-swatch[data-rs-color-swatch]').forEach(sample);
  }

  window.ResinSwatchSampler = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) scan(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
